import { ArrowRight, FilePlus2, FileText } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

function fmtDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" });
}

function cardActionLabel(status) {
  if (status === "pending_payment") return "Continuar";
  if (status === "completed") return "Ver receta";
  return "Ver detalle";
}

export default function PatientDashboard() {
  const { user } = useAuth();
  const [requests, setRequests] = useState(null);

  useEffect(() => {
    api.get("/requests").then((r) => setRequests(r.data)).catch(() => setRequests([]));
  }, []);

  return (
    <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10" data-testid="patient-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
        <div>
          <p className="text-sm text-muted-foreground uppercase tracking-widest mb-1">Mi panel</p>
          <h1 className="text-3xl sm:text-4xl font-bold">Hola, {user.name?.split(" ")[0]}</h1>
        </div>
        <Link to="/nueva-solicitud">
          <Button className="rounded-full" data-testid="dashboard-new-request-btn">
            <FilePlus2 className="h-4 w-4 mr-2" strokeWidth={1.5} /> Nueva solicitud
          </Button>
        </Link>
      </div>

      {requests === null && <p className="text-muted-foreground">Cargando...</p>}

      {requests?.length === 0 && (
        <div className="border rounded-2xl bg-white p-12 text-center" data-testid="patient-empty-state">
          <FileText className="h-10 w-10 text-muted-foreground mx-auto mb-4" strokeWidth={1.5} />
          <h2 className="text-lg font-heading font-semibold mb-2">Aún no tienes solicitudes</h2>
          <p className="text-sm text-muted-foreground mb-6">
            Crea tu primera solicitud de receta THAG: solo necesitas tus laboratorios y tu última receta.
          </p>
          <Link to="/nueva-solicitud">
            <Button className="rounded-full" data-testid="empty-state-new-request-btn">
              Comenzar
            </Button>
          </Link>
        </div>
      )}

      <div className="grid gap-4">
        {requests?.map((r) => (
          <Link
            key={r.id}
            to={r.status === "pending_payment" ? `/nueva-solicitud?continuar=${r.id}` : `/solicitud/${r.id}`}
            data-testid={`request-card-${r.id}`}
            className="border rounded-2xl bg-white p-6 flex flex-wrap items-center justify-between gap-4 card-hover"
          >
            <div>
              <div className="flex items-center gap-3 mb-1">
                <p className="font-heading font-semibold">
                  Receta THAG {r.folio ? `· Folio ${r.folio}` : ""}
                </p>
                <StatusBadge status={r.status} />
              </div>
              <p className="text-sm text-muted-foreground">
                {r.professional_name} · Creada el {fmtDate(r.created_at)}
              </p>
            </div>
            <span className="text-sm font-semibold text-primary flex items-center gap-1">
              {cardActionLabel(r.status)}
              <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
            </span>
          </Link>
        ))}
      </div>
    </main>
  );
}
